import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import Cookies from 'js-cookie'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  
  // State
  const user = ref(null)
  const token = ref(null)
  const refreshToken = ref(null)
  const isLoading = ref(false)
  
  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.user_type === 'admin')
  const isSupervisor = computed(() => user.value?.user_type === 'supervisor')
  const isNormalUser = computed(() => user.value?.user_type === 'normal')
  const canWrite = computed(() => user.value?.can_write ?? false)
  const canCreateUsers = computed(() => user.value?.can_create_users ?? false)
  const userDisplayName = computed(() => user.value?.display_name || 'Usuário')
  const userTypeLabel = computed(() => {
    switch (user.value?.user_type) {
      case 'admin':
        return 'Administrador'
      case 'supervisor':
        return 'Supervisor'
      case 'normal':
        return 'Usuário'
      default:
        return 'Usuário'
    }
  })
  
  // Actions
  async function login(credentials) {
    try {
      isLoading.value = true
      
      const response = await api.post('/auth/login/', credentials)
      const { access, refresh, user: userData } = response.data
      
      // Salvar tokens
      token.value = access
      refreshToken.value = refresh
      user.value = userData
      
      // Salvar nos cookies (para persistência)
      Cookies.set('access_token', access, { expires: 1 }) // 1 dia
      Cookies.set('refresh_token', refresh, { expires: 7 }) // 7 dias
      Cookies.set('user_data', JSON.stringify(userData), { expires: 7 })
      
      // Configurar header padrão
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`
      
      // Redirecionar para dashboard
      router.push({ name: 'Dashboard' })
      
      return { success: true }
      
    } catch (error) {
      const message = error.response?.data?.message || 
                     error.response?.data?.error || 
                     'Erro no login'
      return { success: false, message }
    } finally {
      isLoading.value = false
    }
  }
  
  async function register(userData) {
    try {
      isLoading.value = true
      
      await api.post('/auth/register/', userData)
      
      // Após registro, fazer login automático
      return await login({
        email: userData.email,
        password: userData.password
      })
      
    } catch (error) {
      const message = error.response?.data?.message || 
                     error.response?.data?.error || 
                     'Erro no registro'
      return { success: false, message }
    } finally {
      isLoading.value = false
    }
  }
  
  async function logout() {
    try {
      // Fazer logout no servidor se possível
      if (token.value) {
        await api.post('/auth/logout/')
      }
    } catch (error) {
      console.error('Erro no logout:', error)
    } finally {
      // Limpar estado local
      clearAuth()
      router.push({ name: 'Login' })
    }
  }
  
  function clearAuth() {
    user.value = null
    token.value = null
    refreshToken.value = null
    
    // Limpar cookies
    Cookies.remove('access_token')
    Cookies.remove('refresh_token')
    Cookies.remove('user_data')
    
    // Limpar header
    delete api.defaults.headers.common['Authorization']
  }
  
  async function refreshAuthToken() {
    try {
      if (!refreshToken.value) {
        throw new Error('No refresh token')
      }
      
      const response = await api.post('/auth/refresh/', {
        refresh: refreshToken.value
      })
      
      const { access } = response.data
      token.value = access
      
      // Atualizar cookie
      Cookies.set('access_token', access, { expires: 1 })
      
      // Atualizar header
      api.defaults.headers.common['Authorization'] = `Bearer ${access}`
      
      return true
      
    } catch (error) {
      console.error('Erro ao renovar token:', error)
      clearAuth()
      router.push({ name: 'Login' })
      return false
    }
  }
  
  function initializeAuth() {
    // Tentar restaurar da sessão
    const savedToken = Cookies.get('access_token')
    const savedRefreshToken = Cookies.get('refresh_token')
    const savedUser = Cookies.get('user_data')
    
    if (savedToken && savedRefreshToken && savedUser) {
      try {
        token.value = savedToken
        refreshToken.value = savedRefreshToken
        user.value = JSON.parse(savedUser)
        
        // Configurar header
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`
        
        // Verificar se token ainda é válido
        verifyToken()
        
      } catch (error) {
        console.error('Erro ao restaurar sessão:', error)
        clearAuth()
      }
    }
  }
  
  async function verifyToken() {
    try {
      // Fazer uma requisição simples para verificar se o token é válido
      await api.get('/auth/profile/')
    } catch (error) {
      if (error.response?.status === 401) {
        // Token expirado, tentar renovar
        const renewed = await refreshAuthToken()
        if (!renewed) {
          router.push({ name: 'Login' })
        }
      }
    }
  }
  
  async function updateProfile(profileData) {
    try {
      isLoading.value = true
      
      // Supervisores não podem alterar perfil
      if (isSupervisor.value) {
        return { 
          success: false, 
          message: 'Supervisores não podem alterar o perfil' 
        }
      }
      
      const response = await api.patch('/auth/profile/', profileData)
      user.value = { ...user.value, ...response.data }
      
      // Atualizar cookie
      Cookies.set('user_data', JSON.stringify(user.value), { expires: 7 })
      
      return { success: true }
      
    } catch (error) {
      const message = error.response?.data?.message || 
                     error.response?.data?.error || 
                     'Erro ao atualizar perfil'
      return { success: false, message }
    } finally {
      isLoading.value = false
    }
  }
  
  // Função auxiliar para verificar se usuário pode realizar ação
  function canPerformAction(action) {
    if (!user.value) return false
    
    switch (action) {
      case 'create':
      case 'edit':
      case 'delete':
        return canWrite.value
      case 'create_user':
        return canCreateUsers.value
      case 'view':
        return true // Todos podem visualizar
      default:
        return false
    }
  }
  
  // Função para mostrar mensagem de permissão
  function getPermissionMessage(action) {
    if (isSupervisor.value) {
      return 'Supervisores podem apenas visualizar informações'
    }
    
    switch (action) {
      case 'create_user':
        return 'Apenas administradores podem criar usuários'
      case 'admin_only':
        return 'Esta ação requer privilégios de administrador'
      default:
        return 'Você não tem permissão para realizar esta ação'
    }
  }
  
  return {
    // State
    user,
    token,
    isLoading,
    
    // Getters
    isAuthenticated,
    isAdmin,
    isSupervisor,
    isNormalUser,
    canWrite,
    canCreateUsers,
    userDisplayName,
    userTypeLabel,
    
    // Actions
    login,
    register,
    logout,
    clearAuth,
    refreshAuthToken,
    initializeAuth,
    updateProfile,
    canPerformAction,
    getPermissionMessage
  }
})